import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import iwalkcvService from '../../services/iwalkcv.service';

interface Categoria {
  id: number;
  nome: string;
  slug: string;
  icone?: string;
}

interface FormState {
  nome: string;
  slug: string;
  icone: string;
}

const defaultForm: FormState = { nome: '', slug: '', icone: '' };

const toSlug = (str: string) =>
  str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const IWalkCVCategorias: React.FC = () => {
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(defaultForm);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await iwalkcvService.getCategorias();
      setCategorias(res.data || res);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(defaultForm);
    setFormErrors({});
    setDialogOpen(true);
  };

  const openEdit = (categoria: Categoria) => {
    setEditingId(categoria.id);
    setForm({ nome: categoria.nome, slug: categoria.slug, icone: categoria.icone || '' });
    setFormErrors({});
    setDialogOpen(true);
  };

  const closeDialog = () => { setDialogOpen(false); };

  const validate = (): boolean => {
    const errors: Partial<Record<keyof FormState, string>> = {};
    if (!form.nome.trim()) errors.nome = 'Campo obrigatório';
    if (!form.slug.trim()) errors.slug = 'Campo obrigatório';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = { nome: form.nome, slug: form.slug, icone: form.icone };
      if (editingId !== null) {
        await iwalkcvService.updateCategoria(editingId, payload);
      } else {
        await iwalkcvService.createCategoria(payload);
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleNomeChange = (nome: string) => {
    setForm((prev) => ({
      ...prev,
      nome,
      // auto-gerar slug apenas na criação
      slug: editingId === null ? toSlug(nome) : prev.slug,
    }));
  };

  const openDelete = (id: number) => {
    setDeletingId(id);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (deletingId === null) return;
    setDeleting(true);
    try {
      await iwalkcvService.deleteCategoria(deletingId);
      setDeleteDialogOpen(false);
      await load();
    } catch (e) {
      console.error(e);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Categorias</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Nova Categoria
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>Slug</TableCell>
              <TableCell>Ícone</TableCell>
              <TableCell align="right">Acções</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : categorias.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Nenhuma categoria encontrada.
                </TableCell>
              </TableRow>
            ) : (
              categorias.map((cat) => (
                <TableRow key={cat.id} hover>
                  <TableCell>{cat.id}</TableCell>
                  <TableCell>{cat.nome}</TableCell>
                  <TableCell>{cat.slug}</TableCell>
                  <TableCell sx={{ fontSize: 20 }}>{cat.icone || '—'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => openEdit(cat)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton size="small" color="error" onClick={() => openDelete(cat.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{editingId !== null ? 'Editar Categoria' : 'Nova Categoria'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Nome"
            value={form.nome}
            onChange={(e) => handleNomeChange(e.target.value)}
            error={!!formErrors.nome}
            helperText={formErrors.nome}
            fullWidth
            autoFocus
          />
          <TextField
            label="Slug"
            value={form.slug}
            onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))}
            error={!!formErrors.slug}
            helperText={formErrors.slug || (editingId === null ? 'Auto-gerado a partir do nome' : undefined)}
            fullWidth
          />
          <TextField
            label="Ícone (emoji ou nome)"
            value={form.icone}
            onChange={(e) => setForm((p) => ({ ...p, icone: e.target.value }))}
            fullWidth
            placeholder="ex: 🏄 ou waves"
            helperText="Um emoji (🏄) OU um destes nomes: anchor, mountain, landmark, utensils, waves, bus, camera, feather, compass, hotel, fish, car, briefcase, tent, ship, sailboat, plane, bike, coffee, music, sun, umbrella, ticket, heart, wine, star"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar eliminação</DialogTitle>
        <DialogContent>
          <Typography>Tem a certeza que pretende eliminar esta categoria? Esta acção é irreversível.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={18} /> : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IWalkCVCategorias;
